import { useCallback, useEffect, useState } from 'react'
import { useAuthToken } from '@core/pages/admin/useAuthToken'
import { CLOUD_API } from '@core/pages/admin/shared'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, th } from '@ui/classes'
import { errBanner, okBanner, sectionCard } from '@ui/admin'

interface Withdrawal {
  id: string
  receipt_id: string | null
  line_id: string | null
  code: string
  mode: 'ofd' | 'true_api'
  reason: string
  status: 'pending' | 'sent' | 'applied' | 'error' | 'skipped'
  attempts: number
  last_error: string | null
}

const STATUS_LABEL: Record<Withdrawal['status'], string> = {
  pending: 'Ожидает',
  sent: 'Отправлен',
  applied: 'Выведен',
  error: 'Ошибка',
  skipped: 'Пропущен'
}

const STATUS_CLASS: Record<Withdrawal['status'], string> = {
  pending: 'text-amber-700',
  sent: 'text-amber-700',
  applied: 'text-okDark',
  error: 'text-danger',
  skipped: 'text-muted'
}

/** Очередь вывода кодов ЧЗ: сверка ОФД и fail-closed True API. */
export function WithdrawalsSection() {
  const token = useAuthToken()
  const [rows, setRows] = useState<Withdrawal[]>([])
  const [metrics, setMetrics] = useState<Record<string, number>>({})
  const [status, setStatus] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!token) return
    setBusy(true)
    setError(null)
    try {
      const query = new URLSearchParams({ limit: '200' })
      if (status) query.set('status', status)
      const [listRes, metricsRes] = await Promise.all([
        fetch(`${CLOUD_API}/cz/withdrawals?${query}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${CLOUD_API}/cz/withdrawals/metrics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ])
      if (!listRes.ok || !metricsRes.ok) throw new Error(`HTTP ${listRes.status}/${metricsRes.status}`)
      const listBody = (await listRes.json()) as { withdrawals: Withdrawal[] }
      const metricsBody = (await metricsRes.json()) as { metrics: Record<string, number> }
      setRows(listBody.withdrawals ?? [])
      setMetrics(metricsBody.metrics ?? {})
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [status, token])

  useEffect(() => {
    void load()
  }, [load])

  const retry = async (id: string) => {
    if (!token) return
    setBusy(true)
    setError(null)
    try {
      const response = await fetch(`${CLOUD_API}/cz/withdrawals/${id}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      setMessage('Код возвращён в очередь')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <div className={sectionCard}>
        <h3 className="mt-0 text-big font-bold">Вывод кодов ЧЗ</h3>
        <p className={hint}>Нужен токен облачной учётной записи.</p>
      </div>
    )
  }

  return (
    <div className={sectionCard}>
      <h3 className="mt-0 text-big font-bold">Вывод кодов ЧЗ</h3>
      <p className={hint}>
        Фискальная продажа — сверка ОФД. Чеки без ККТ, списание и возврат поставщику —
        True API; без подтверждённого метода очередь остаётся в ошибке и не считается выведенной.
      </p>

      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="text-small">
          Статус
          <select
            className="mt-1 block rounded border border-line bg-white px-2 py-1.5 text-small"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Все</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <button
          className={cx(btnBase, btn.secondary, btnSize.sm)}
          onClick={() => void load()}
          disabled={busy}
        >
          {busy ? 'Загрузка…' : 'Обновить'}
        </button>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 text-small sm:grid-cols-5">
        {(['pending', 'sent', 'applied', 'error', 'skipped'] as const).map((key) => (
          <div key={key} className="rounded border border-line p-2">
            <div className="text-muted">{STATUS_LABEL[key]}</div>
            <strong>{metrics[key] ?? 0}</strong>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className={okBanner}>Очередь пуста</p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Код</th>
                <th className={th}>Режим</th>
                <th className={th}>Причина</th>
                <th className={th}>Статус</th>
                <th className={th}>Попытки</th>
                <th className={th}>Ошибка</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={td}>
                    <code>
                      {row.code.slice(0, 32)}
                      {row.code.length > 32 ? '…' : ''}
                    </code>
                  </td>
                  <td className={td}>{row.mode}</td>
                  <td className={td}>{row.reason}</td>
                  <td className={cx(td, STATUS_CLASS[row.status])}>{STATUS_LABEL[row.status]}</td>
                  <td className={td}>{row.attempts}</td>
                  <td className={td}>{row.last_error ?? '—'}</td>
                  <td className={td}>
                    {(row.status === 'error' || row.status === 'skipped') && (
                      <button
                        className={cx(btnBase, btn.secondary, btnSize.sm)}
                        onClick={() => void retry(row.id)}
                        disabled={busy}
                      >
                        Повторить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

